// Pure care-plan formatting helpers — shared by CarePlanTab (read + edit paths)
// and the buildEditPlan unit test. No React, no I/O.

import { TARGET_OP_COMPARISON } from '@/logic/patientHome';

// A poisoned display starts with a non-comparison operator name (e.g. "reduce_by_pct14"),
// a relic of edit-mode's old naive `${operator}${value}` concatenation.
export function isPoisoned(display, op) {
  if (!display || !op || TARGET_OP_COMPARISON.test(op)) return false;
  return String(display).startsWith(op);
}

// Heal poisoned `display` strings. Call in BOTH render paths so read + edit stay in sync.
export function formatTargetDisplay(rawVal) {
  if (!rawVal || typeof rawVal !== 'object') return String(rawVal ?? '');
  const op = String(rawVal.operator || '').trim();
  const val = rawVal.value;
  if (TARGET_OP_COMPARISON.test(op)) {
    return val != null && val !== '' ? `${op}${val}` : (rawVal.display && !rawVal.display.startsWith(op + op) ? rawVal.display : '—');
  }
  if (op === 'reduce_by_pct') return val != null && val !== '' ? `Reduce by ${val}%` : (isPoisoned(rawVal.display, op) ? '5-10% reduction' : rawVal.display || '5-10% reduction');
  if (op === 'reduce')        return val != null && val !== '' ? `Reduce by ${val}` : (isPoisoned(rawVal.display, op) ? 'Reduce' : rawVal.display || 'Reduce');
  if (op === 'stable')        return isPoisoned(rawVal.display, op) ? 'Maintain stable' : (rawVal.display || 'Maintain stable');
  if (rawVal.display && !isPoisoned(rawVal.display, op)) return rawVal.display;
  return String(val ?? rawVal.display ?? '');
}

// apiv2 stores the approver in an integer user-id column, so a name round-trips to
// "0" (or a bare BIGINT) on reload. Show "by <x>" only for a real (non-all-digits) name.
export function approverDisplayName(approvedBy) {
  return approvedBy && !/^\d+$/.test(String(approvedBy).trim()) ? String(approvedBy) : '';
}

// Build the editable draft from a care plan, coercing each field to its expected
// shape. `|| []` is NOT enough — a truthy non-array (e.g. medications stored as `{}`)
// survives it and then `.map` throws in edit mode. Force the type.
const asArr = (v) => (Array.isArray(v) ? v : []);
const asObj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
export function buildEditPlan(cp) {
  return {
    conditions: asArr(cp.conditions),
    risk_level: cp.risk_level || 'standard',
    targets: asObj(cp.targets),
    devices: asArr(cp.devices),
    lifestyle_focus: asArr(cp.lifestyle_focus),
    daily_tasks: asArr(cp.daily_tasks),
    exercise_plan: asObj(cp.exercise_plan),
    nutrition_plan: asObj(cp.nutrition_plan),
    medications: asArr(cp.medications),
    content_curriculum: asArr(cp.content_curriculum),
  };
}
